import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Loader2, Pencil, Trash2, Eye } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import InviteDialog from "@/components/InviteDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const EMPTY = { name: "", email: "", phone: "", gender: "Kadın", goal: "", height: "", weight: "", status: "active", trainer_id: "none" };

export default function Members() {
  const navigate = useNavigate();
  const [members, setMembers] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [delId, setDelId] = useState(null);
  const [invite, setInvite] = useState(null);

  const load = () => api.get("/members").then((r) => setMembers(r.data));

  useEffect(() => {
    load();
    api.get("/trainers").then((r) => setTrainers(r.data));
    api.get("/branches").then((r) => setBranches(r.data));
  }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (m) => {
    setEditing(m);
    setForm({ name: m.name, email: m.email || "", phone: m.phone || "", gender: m.gender || "Kadın", goal: m.goal || "", height: m.height || "", weight: m.weight || "", status: m.status, trainer_id: m.trainer_id || "none" });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name, phone: form.phone, gender: form.gender, goal: form.goal,
        height: form.height ? Number(form.height) : null, weight: form.weight ? Number(form.weight) : null,
        status: form.status, trainer_id: form.trainer_id === "none" ? null : form.trainer_id,
      };
      if (editing) {
        await api.put(`/members/${editing.id}`, payload);
        toast.success("Üye güncellendi");
      } else {
        const res = await api.post("/members", { ...payload, email: form.email, branch_id: branches[0]?.id });
        toast.success("Üye oluşturuldu");
        if (res.data?.invite_token) setInvite({ name: res.data.name || form.name, token: res.data.invite_token });
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try { await api.delete(`/members/${delId}`); toast.success("Üye silindi"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setDelId(null); }
  };

  const filtered = (members || []).filter((m) => m.name.toLowerCase().includes(q.toLowerCase()) || (m.email || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-members-page">
      <PageHeader title="Üyeler" subtitle={`${members?.length ?? "—"} üye kayıtlı`}
        action={<Button onClick={openNew} data-testid="add-new-member-button" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold rounded-xl"><Plus size={18} className="mr-1" /> Yeni Üye</Button>} />

      <div className="relative mb-4 max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#68687B]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="members-table-search-input" placeholder="Üye ara..."
          className="pl-9 bg-[#0E0E14] border-[#262636] text-white h-11 focus-visible:ring-[#FF5500]" />
      </div>

      {members === null ? (
        <div className="space-y-2">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 rounded-xl bg-[#13131C]" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-[#68687B]">Üye bulunamadı.</div>
      ) : (
        <div className="rounded-2xl border border-[#222230] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="members-table">
              <thead className="bg-[#181824]">
                <tr className="text-left text-[#68687B] text-xs uppercase tracking-wide">
                  <th className="px-4 py-3 font-semibold">Üye</th>
                  <th className="px-4 py-3 font-semibold hidden md:table-cell">Antrenör</th>
                  <th className="px-4 py-3 font-semibold hidden lg:table-cell">Paket</th>
                  <th className="px-4 py-3 font-semibold hidden sm:table-cell">Durum</th>
                  <th className="px-4 py-3 font-semibold text-right">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m, i) => (
                  <tr key={m.id} data-testid={`member-row-${m.id}`} className={`border-t border-[#1A1A24] hover:bg-[#1B1B27] transition-colors ${i % 2 ? "bg-[#161622]" : "bg-[#13131C]"}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-9 h-9"><AvatarFallback className="bg-[#1B1B27] text-[#FF5500] text-xs font-bold">{m.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}</AvatarFallback></Avatar>
                        <div><p className="font-semibold text-white">{m.name}</p><p className="text-xs text-[#68687B]">{m.email}</p></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#A0A0B2] hidden md:table-cell">{m.trainer_name || "—"}</td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      {m.package_name ? <span className="text-[#A0A0B2]">{m.package_name} · <span className="text-[#FF5500] font-mono">{m.remaining_lessons}/{m.total_lessons}</span></span> : "—"}
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell"><StatusBadge status={m.status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => navigate(`/admin/members/${m.id}`)} data-testid={`view-member-${m.id}`} className="p-2 rounded-lg text-[#68687B] hover:text-white hover:bg-[#252536]"><Eye size={16} /></button>
                        <button onClick={() => openEdit(m)} data-testid={`edit-member-${m.id}`} className="p-2 rounded-lg text-[#68687B] hover:text-[#FF5500] hover:bg-[#252536]"><Pencil size={16} /></button>
                        <button onClick={() => setDelId(m.id)} data-testid={`delete-member-${m.id}`} className="p-2 rounded-lg text-[#68687B] hover:text-[#EF4444] hover:bg-[#252536]"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#13131C] border-[#222230] text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Üye Düzenle" : "Yeni Üye"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <Field label="Ad Soyad" full><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="member-form-name" className="bg-[#0E0E14] border-[#262636] text-white" /></Field>
            {!editing && <Field label="E-posta" full><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="member-form-email" className="bg-[#0E0E14] border-[#262636] text-white" /></Field>}
            <Field label="Telefon"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></Field>
            <Field label="Cinsiyet">
              <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger className="bg-[#0E0E14] border-[#262636] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#181824] border-[#2D2D3F] text-white"><SelectItem value="Kadın">Kadın</SelectItem><SelectItem value="Erkek">Erkek</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Boy (cm)"><Input type="number" value={form.height} onChange={(e) => setForm({ ...form, height: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></Field>
            <Field label="Kilo (kg)"><Input type="number" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></Field>
            <Field label="Antrenör" full>
              <Select value={form.trainer_id} onValueChange={(v) => setForm({ ...form, trainer_id: v })}>
                <SelectTrigger data-testid="member-form-trainer" className="bg-[#0E0E14] border-[#262636] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#181824] border-[#2D2D3F] text-white">
                  <SelectItem value="none">Atanmamış</SelectItem>
                  {trainers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Hedef" full><Input value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></Field>
            <Field label="Durum" full>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-[#0E0E14] border-[#262636] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#181824] border-[#2D2D3F] text-white"><SelectItem value="active">Aktif</SelectItem><SelectItem value="passive">Pasif</SelectItem><SelectItem value="frozen">Donduruldu</SelectItem></SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-[#A0A0B2]">İptal</Button>
            <Button onClick={save} disabled={saving || !form.name} data-testid="member-form-submit" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold">
              {saving ? <Loader2 className="animate-spin" size={16} /> : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={() => setDelId(null)}>
        <AlertDialogContent className="bg-[#13131C] border-[#222230] text-white">
          <AlertDialogHeader><AlertDialogTitle>Üyeyi sil?</AlertDialogTitle><AlertDialogDescription className="text-[#A0A0B2]">Bu işlem geri alınamaz. Üye ve giriş hesabı silinecek.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#1B1B27] border-[#2D2D3F] text-white hover:bg-[#252536]">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-member" className="bg-[#EF4444] hover:bg-[#dc2626] text-white">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <InviteDialog invite={invite} onClose={() => setInvite(null)} />
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="text-xs font-medium text-[#A0A0B2] mb-1 block">{label}</label>
      {children}
    </div>
  );
}
